import React from 'react';
import { motion } from 'framer-motion';
import { LazyImage } from '@/components/ui/lazy-image';
const RecognitionProblem = () => {
  const engagementStats = [
    "More productive at work",
    "More loyal to the company", 
    "More invested in outcomes"
  ];
  return <section className="py-7 sm:py-10 lg:pt-0 lg:pb-12 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        
        {/* Modern Container */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 lg:p-12 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-[7.5rem] h-[7.5rem] bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-12 translate-x-12 opacity-80"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-orange-100 to-yellow-100 rounded-full translate-y-12 -translate-x-12 opacity-80"></div>
          
          <div className="relative z-10">
        <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-7">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-800 mb-4 max-w-3xl mx-auto leading-relaxed">
            Recognition Is Still <span className="text-blue-600">Missing</span><br />Where It Matters Most
          </h2>
        </motion.div>

        {/* Top Section - Text Left, Image Right */}
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-7 items-center mb-6 relative">
          
          {/* Left Side - Problem Statement */}
          <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="space-y-4 -mt-2">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-800 leading-tight">
              Nearly 7 in 10 employees say they don't get the recognition they deserve.
            </h3>
            
            <p className="text-base text-gray-600">When appreciation is inconsistent or invisible, people disengage. Recognition isn't a perk anymore — it's how you keep your best people connected and motivated.</p>
          </motion.div>

          {/* Right Side - Sad Employee Image */}
          <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex justify-start ml-6">
            <LazyImage src="/lovable-uploads/f8787b99-f33a-47b4-b3d5-7580404b4681.png" alt="Employee lacking recognition" className="w-44 h-auto object-contain" />
          </motion.div>
        </div>

        {/* Bottom Section - Image Left, Text Right */}
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-7 items-center mt-12 relative">
          
          {/* Left Side - Happy Employee Image */}
          <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="flex justify-center ml-8">
            <LazyImage src="/lovable-uploads/231f130b-c5ff-445d-adf8-795b76a2ae8a.png" alt="Happy engaged employee" className="w-64 h-auto object-contain" />
          </motion.div>

          {/* Right Side - Stats */}
          <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="space-y-4">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-800 leading-tight">
              Engaged Teams Stay Longer - and <span className="text-blue-600">Perform Better</span>
            </h3>
            <p className="text-base text-gray-600 mt-2">When employees feel seen and valued, they show up stronger:</p>

            <div className="space-y-3">
              {engagementStats.map((stat, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.4 + index * 0.1
                }} className="flex items-center space-x-2">
                  <div className="text-base text-gray-700">
                    • {stat}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        {/* Data Attribution */}
        <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 1
          }} className="text-right mt-7">
          <p className="text-xs text-gray-500 italic">*Data from Gallup</p>
        </motion.div>
          </div>
        </div>
      </div>
    </section>;
};
export default RecognitionProblem;